import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { ConversationProps } from '../../../../../../data/message/domain/conversation/conversation';
import {
  Profile,
  ProfileProps,
} from '../../../../../../data/message/domain/profile/profile';
import { Patient } from '../../../../../../data/patient/domain/patient';
import {
  PatientInfoViewModel,
  SendQuestionModalFacade,
} from './send-question-modal.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-send-question-modal',
  templateUrl: './send-question-modal.component.html',
  styleUrls: ['./send-question-modal.component.scss'],
  providers: [SendQuestionModalFacade],
})
export class SendQuestionModalComponent implements OnInit, OnDestroy {
  @Input() target: Patient | Patient[] | PatientGroup | undefined;
  @Input() archived = false;
  @Output() cancel = new EventEmitter();
  @Output() send = new EventEmitter();

  public form: FormGroup;
  public isLoading = false;
  public error?: string;
  public dietitian: Dietitian;
  private patientStatusSubscription?: Subscription;
  private viewModel$: Observable<PatientInfoViewModel>;

  public get name() {
    if (this.target) {
      if (Array.isArray(this.target)) {
        if (this.target.length > 1) {
          return 'aux ' + this.target.length + ' patients';
        } else {
          return (
            'à ' + this.target[0].firstName + ' ' + this.target[0].lastName
          );
        }
      } else {
        if (this.target instanceof Patient) {
          return 'à ' + this.target.firstName + ' ' + this.target.lastName;
        } else {
          return 'aux patients du groupe "' + this.target.name + '"';
        }
      }
    } else {
      return 'à tous les patients';
    }
  }

  get isScheduled(): boolean {
    return this.form.get('scheduled')?.value;
  }

  get isFormValid(): boolean {
    if (!this.form.get('scheduled')?.value) {
      return this.form.valid;
    } else {
      return (
        this.form.valid &&
        this.form.get('date')?.value &&
        this.form.get('time')?.value
      );
    }
  }

  constructor(
    private bsModalRef: BsModalRef,
    private facade: SendQuestionModalFacade,
  ) {
    this.viewModel$ = this.facade.viewModel$;
  }

  ngOnDestroy(): void {
    this.patientStatusSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      subject: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
      ]),
      message: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
      ]),
      scheduled: new FormControl(false),
      date: new FormControl(''),
      time: new FormControl(''),
    });

    this.patientStatusSubscription = this.viewModel$.subscribe((state) => {
      // if (!this.target) {
      //   this.target = state.patient;
      // }
      this.dietitian = state.dietitian;
    });
  }

  hideModal() {
    this.bsModalRef.hide();
  }

  async sendMessage() {
    if (this.target) {
      if (Array.isArray(this.target)) {
        await this.sendMessageToPatientSelection();
      } else {
        if (this.target instanceof Patient) {
          await this.sendMessageToSpecificPatient();
        } else {
          await this.sendMessagesToGroupedPatients();
        }
      }
    } else {
      await this.sendMessagesToGroupedPatients();
    }
  }

  private async sendMessagesToGroupedPatients() {
    const subject = this.form.get('subject')?.value;
    const message = this.form.get('message')?.value;

    if (!subject || !message) {
      return;
    }

    this.error = undefined;
    this.isLoading = true;

    try {
      if (this.isScheduled) {
        const date: Date = this.form.get('date')?.value;
        const time: string = this.form.get('time')?.value;
        await this.facade.scheduleMessagesToGroupedPatients(
          this.dietitian,
          subject,
          message,
          this.target as PatientGroup,
          date,
          time,
          this.archived,
        );
      } else {
        await this.facade.sendMessagesToGroupedPatients(
          subject,
          message,
          this.target as PatientGroup,
          this.archived,
        );
      }
      this.send?.emit();
      this.hideModal();
    } catch (err: any) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  private async sendMessageToSpecificPatient() {
    const subject = this.form.get('subject')?.value;
    const message = this.form.get('message')?.value;

    if (
      !(this.target instanceof Patient) ||
      !this.target?.patientId ||
      !this.dietitian ||
      !subject ||
      !message
    ) {
      return;
    }

    // refresh patient to ensure data are up to date (especially user id)
    const patient = await this.facade.getPatient(
      this.target.patientId.id.toString(),
    );

    this.error = undefined;
    this.isLoading = true;

    const props = {
      patient: Profile.create({
        profileId: patient.patientId,
        userId: patient.userId,
        firstName: patient.firstName,
        lastName: patient.lastName,
        profilePicture: patient.avatarUrl,
        hasNewMessage: true,
      } as ProfileProps),
      dietitian: Profile.create({
        profileId: this.dietitian.dietitianId,
        userId: this.dietitian.userId,
        firstName: this.dietitian.firstName,
        lastName: this.dietitian.lastName,
        profilePicture: this.dietitian.picture?.url,
        hasNewMessage: false,
        lastMessageReadDate: new Date(),
      } as ProfileProps),
      subject: subject,
    } as ConversationProps;

    try {
      if (this.isScheduled) {
        const date: Date = this.form.get('date')?.value;
        const time: string = this.form.get('time')?.value;
        await this.facade.scheduleQuestion(
          this.dietitian,
          props,
          message,
          date,
          time,
        );
      } else {
        await this.facade.sendQuestion(props, message);
      }
      this.send?.emit();
      this.hideModal();
    } catch (err: any) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  private async sendMessageToPatientSelection() {
    const subject = this.form.get('subject')?.value;
    const message = this.form.get('message')?.value;

    if (!subject || !message) {
      return;
    }

    this.error = undefined;
    this.isLoading = true;

    try {
      if (this.isScheduled) {
        const date: Date = this.form.get('date')?.value;
        const time: string = this.form.get('time')?.value;
        await this.facade.scheduleMessagesToPatientSelection(
          this.dietitian,
          subject,
          message,
          this.target as Patient[],
          date,
          time,
        );
      } else {
        await this.facade.sendMessagesToPatientSelection(
          subject,
          message,
          this.target as Patient[],
        );
      }
      this.send?.emit();
      this.hideModal();
    } catch (err: any) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  get subjectInvalid(): boolean | undefined {
    return (
      !this.form.get('subject')?.valid &&
      (this.form.get('subject')?.touched || this.form.get('subject')?.dirty)
    );
  }

  get messageInvalid(): boolean | undefined {
    return (
      !this.form.get('message')?.valid &&
      (this.form.get('message')?.touched || this.form.get('message')?.dirty)
    );
  }
}
